import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import accounting from 'accounting'

// Shared
import FormatUtils from 'shared/utils/format'

// Utils
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'

// UI
import BInput from 'ui/blocks/Input'
import Span from 'ui/elements/Span'

const renderValue = (value, cents) => {
  if (value == null) return ''

  return cents ? FormatUtils.money(value, { symbol: '' }) : value
}

const PriceInput = ({
  id,
  currency,
  cents,
  hidePrefix,
  field,
  size = 'md',
  className,
  error,
  inputRef,
  disabled,
  maxValue = null,
  ...otherProps
}) => {
  const [displayValue, setDisplayValue] = useState(undefined)
  const inputElement = useRef(null)

  useEffect(() => {
    if (field.value != null) {
      if (cents) {
        if (field.value !== FormatUtils.parsePriceToCents(displayValue)) {
          setDisplayValue(FormatUtils.money(field.value, { format: '%v' }))
        }
      } else {
        setDisplayValue(field.value)
      }
    }
  }, [field.value, cents, displayValue])

  const handleChange = (event) => {
    const value = event.target.value

    if (isEmpty(trim(value))) {
      field.onChange(null)
      setDisplayValue('')
    } else if (cents) {
      const valueInCents = FormatUtils.parsePriceToCents(value)
      field.onChange(valueInCents)
      setDisplayValue(value)
    } else {
      field.onChange(value)
      setDisplayValue(value)
    }
  }

  const handleBlur = (event) => {
    // Call the field's onBlur handler if it exists
    if (field.onBlur) {
      field.onBlur(event)
    }

    if (isEmpty(trim(displayValue))) {
      return
    }

    if (cents) {
      // Format the displayValue to a proper money format
      const valueInCents = FormatUtils.parsePriceToCents(displayValue)
      const formattedValue = FormatUtils.money(valueInCents, { symbol: '' })
      setDisplayValue(formattedValue)
    } else {
      // Format as regular number
      const numericValue = FormatUtils.parseNumber(displayValue)
      // Update the field value with the parsed numeric value
      field.onChange(numericValue)
      setDisplayValue(FormatUtils.number(numericValue))
    }
  }

  const finalValue = displayValue || renderValue(field.value, cents)
  const resolvedCurrency = currency || accounting.settings.currency.symbol
  const suffixValue = maxValue && renderValue(maxValue, cents)

  const Prefix = () => {
    return (
      <BInput.Prefix>
        <Span modifiers="font-size-md text-Text/Primary font-weight-semibold">{resolvedCurrency}</Span>
      </BInput.Prefix>
    )
  }

  const Suffix = () => {
    return (
      <BInput.Suffix>
        <Span modifiers="font-size-md text-Text/Tertiary">/ {resolvedCurrency}{suffixValue}</Span>
      </BInput.Suffix>
    )
  }

  const hasMaxValue = maxValue !== null

  return (
    <BInput size={size} hasSuffix={hasMaxValue}>
      {resolvedCurrency && !hidePrefix && <Prefix />}
      <BInput.Input
        id={id}
        ref={(ref) => {
          inputElement.current = ref
          if (inputRef) inputRef(ref)
        }}
        {...field}
        type="text"
        onChange={handleChange}
        onBlur={handleBlur}
        value={finalValue}
        size={size}
        error={error}
        className={className}
        disabled={disabled}
        {...otherProps}
      />
      {resolvedCurrency && hasMaxValue && <Suffix />}
    </BInput>
  )
}

PriceInput.propTypes = {
  id: PropTypes.string,
  currency: PropTypes.string,
  cents: PropTypes.bool,
  hidePrefix: PropTypes.bool,
  field: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  error: PropTypes.any,
  inputRef: PropTypes.func,
  disabled: PropTypes.bool,
  maxValue: PropTypes.number
}

export default PriceInput
